/**
 * UniversalSearch - Cmd+K command palette for searching aircraft, clients, spaces
 *
 * Uses shadcn CommandDialog (built on cmdk). Shows grouped results with
 * Focus and Highlight actions. Triggered by Cmd+K or clicking the search input.
 */

import { useState, useCallback, useEffect } from 'react'
import { Plane, Users, Building2, Crosshair, Highlighter, MapPin, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command'
import { useUniversalSearch } from '@/hooks/useUniversalSearch'
import type { AircraftSearchResult, SpaceSearchResult } from '@/lib/api/universal-search'

interface UniversalSearchProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onFocusAircraft: (allocationId: string) => void
  onHighlightAircraft: (allocationIds: string[]) => void
  onFocusArea: (areaId: string) => void
}

export function UniversalSearch({
  open,
  onOpenChange,
  onFocusAircraft,
  onHighlightAircraft,
  onFocusArea,
}: UniversalSearchProps) {
  const [query, setQuery] = useState('')
  const { results, isLoading, isEmpty } = useUniversalSearch(query)

  // Clear query when dialog closes
  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const handleFocusAircraft = useCallback(
    (result: AircraftSearchResult) => {
      onFocusAircraft(result.allocationId)
      onOpenChange(false)
    },
    [onFocusAircraft, onOpenChange]
  )

  const handleHighlightAircraft = useCallback(
    (allocationIds: string[]) => {
      onHighlightAircraft(allocationIds)
      onOpenChange(false)
    },
    [onHighlightAircraft, onOpenChange]
  )

  const handleFocusArea = useCallback(
    (result: SpaceSearchResult) => {
      onFocusArea(result.id)
      onOpenChange(false)
    },
    [onFocusArea, onOpenChange]
  )

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search"
      description="Search aircraft, clients, and spaces"
      showCloseButton={false}
    >
      <CommandInput
        placeholder="Search everything... (tail, client, space)"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {isLoading && query.length >= 2 && (
          <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Searching...
          </div>
        )}

        {isEmpty && <CommandEmpty>No results found.</CommandEmpty>}

        {/* Aircraft results */}
        {results.aircraft.length > 0 && (
          <CommandGroup heading="Aircraft">
            {results.aircraft.map((result) => (
              <CommandItem
                key={result.allocationId}
                value={`aircraft-${result.tailNumber}`}
                onSelect={() => handleFocusAircraft(result)}
                className="flex items-start gap-3 py-2"
              >
                <Plane className="w-4 h-4 mt-0.5 text-blue-500" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{result.tailNumber}</span>
                    <span className="text-xs text-muted-foreground">
                      {[result.manufacturer, result.model].filter(Boolean).join(' ') || result.typeCode}
                    </span>
                    {result.contractType && (
                      <Badge
                        variant="secondary"
                        className={`text-[10px] px-1 ${
                          result.contractType === 'lease' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {result.contractType === 'lease' ? 'Lease' : 'Transient'}
                      </Badge>
                    )}
                  </div>
                  {(result.ownerName || result.areaName) && (
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                      {result.ownerName && <span>{result.ownerName}</span>}
                      {result.areaName && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="w-3 h-3" />
                          {result.areaName}
                          {result.unitCode && ` · ${result.unitCode}`}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    className="p-1 rounded hover:bg-accent transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleFocusAircraft(result)
                    }}
                    title="Focus on map"
                  >
                    <Crosshair className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                  <button
                    className="p-1 rounded hover:bg-accent transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleHighlightAircraft([result.allocationId])
                    }}
                    title="Highlight on map"
                  >
                    <Highlighter className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.aircraft.length > 0 && results.clients.length > 0 && (
          <CommandSeparator />
        )}

        {/* Client results */}
        {results.clients.length > 0 && (
          <CommandGroup heading="Clients">
            {results.clients.map((result) => (
              <CommandItem
                key={result.id}
                value={`client-${result.displayName}`}
                className="flex items-start gap-3 py-2"
              >
                <Users className="w-4 h-4 mt-0.5 text-green-500" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{result.displayName}</span>
                  <div className="text-xs text-muted-foreground">
                    {result.clientType}
                    {result.aircraftCount > 0 && ` · ${result.aircraftCount} aircraft`}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {(results.aircraft.length > 0 || results.clients.length > 0) &&
          results.spaces.length > 0 && <CommandSeparator />}

        {/* Space results */}
        {results.spaces.length > 0 && (
          <CommandGroup heading="Spaces">
            {results.spaces.map((result) => (
              <CommandItem
                key={result.id}
                value={`space-${result.name}`}
                onSelect={() => handleFocusArea(result)}
                className="flex items-start gap-3 py-2"
              >
                <Building2 className="w-4 h-4 mt-0.5 text-purple-500" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{result.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">{result.code}</span>
                  {result.parentName && (
                    <div className="text-xs text-muted-foreground">{result.parentName}</div>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Quick actions when empty */}
        {query.length < 2 && (
          <CommandGroup heading="Quick Actions">
            <CommandItem className="text-muted-foreground text-xs">
              Type a tail number (N...), client name, or space name to search
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  )
}
